


























































































import useContactsLists from "@/composables/useContactsLists";
import ContactInterface from "@/interfaces/contact.interface";
import { ContactFilterInterface } from "@/interfaces/contactFilter.interface";
import ToastificationContent from "@/layouts/components/toastification/toastificationContent.vue";

// @ts-ignore
import i18n from "@/libs/i18n";
import { styleByColor } from "@/utils/color";
import { computed, defineComponent, nextTick, ref, watch } from "@vue/composition-api";

import { BBadge, BButton, BCol, BFormCheckbox, BFormInput, BModal, BRow, BTable } from "bootstrap-vue";
import { useToast } from "vue-toastification/composition";
import ContactFilters from "./contactFilters.vue";

import ContactListsExisting from "./contactListsExisting.vue";

export default defineComponent({
    components: {
        BModal,
        BButton,
        BTable,
        BBadge,
        BFormCheckbox,
        BFormInput,
        BCol,
        BRow,
        ContactListsExisting,
        ContactFilters,
    },
    props: {
        contactListId: {
            required: true,
            type: Number,
        },
    },
    setup(_, context) {
        const bvModal = context.root.$bvModal;
        const { contactsList, columns } = useContactsLists(bvModal, true);
        const toast = useToast();

        const selectedContacts = ref<ContactInterface[]>([]);
        const currentSelected = ref<number[]>([]);
        const contactsTable = ref<BTable>();
        const searchQuery = ref("");
        const selectedAll = ref(false);
        const filters = ref<ContactFilterInterface>({
            labels: [],
            filterOption: "or", // Default filter behaviour is show results with at least one selected label
        });

        const updateSelection = (contacts: ContactInterface[]) => {
            selectedContacts.value = contacts;
        };

        const updateContacts = () => {
            if (!contactsList.value) {
                return;
            }

            contactsList.value.contacts = selectedContacts.value.map(
                (newContact) =>
                    contactsList.value?.contacts.find((contact) => contact.id === newContact.id) ?? newContact
            );

            toast({
                component: ToastificationContent,
                props: {
                    title: i18n.t("contacts.lists.contacts-lists"),
                    text: i18n.t("contacts.lists.edit.contacts-updated"),
                    icon: "check",
                    variant: "success",
                },
            });
        };

        const clickedRow = (clickedContact: ContactInterface, index: number) => {
            const isSelected = contactsTable.value?.isRowSelected(index);

            if (isSelected) {
                contactsTable.value?.unselectRow(index);

                currentSelected.value = currentSelected.value.filter((contact) => contact !== clickedContact.id);
            } else {
                contactsTable.value?.selectRow(index);

                // Only add the contact if it isn't in the array already
                if (!currentSelected.value.find((contact) => contact === clickedContact.id)) {
                    currentSelected.value = [...currentSelected.value, clickedContact.id];
                }
            }
        };

        const refreshSelection = () => {
            filteredContacts.value.forEach((contact) => {
                const tableContactIndex = contactsTable.value?.localItems.findIndex(
                    (tableContact: ContactInterface) => tableContact.id === contact.id
                );

                if (!~tableContactIndex) {
                    return;
                }

                if (currentSelected.value.includes(contact.id)) {
                    contactsTable.value?.selectRow(tableContactIndex);
                } else {
                    contactsTable.value?.unselectRow(tableContactIndex);
                }
            });
        };

        const removeContacts = () =>
            bvModal
                .msgBoxConfirm(i18n.tc("contacts.lists.edit.remove-confirmation", currentSelected.value.length), {
                    title: i18n.t("confirm.Are you sure?"),
                    cancelTitle: i18n.t("confirm.Cancel"),
                    cancelVariant: "light",
                    okTitle: i18n.t("confirm.remove"),
                    hideHeaderClose: false,
                    headerCloseVariant: "light",
                    centered: true,
                })
                .then((result) => {
                    if (!result || !contactsList.value) {
                        return;
                    }

                    contactsList.value.contacts = contactsList.value.contacts.filter(
                        (contact) => !currentSelected.value.includes(contact.id)
                    );

                    currentSelected.value = [];

                    toast({
                        component: ToastificationContent,
                        props: {
                            title: i18n.t("contacts.lists.contacts-lists"),
                            text: i18n.t("contacts.lists.edit.contacts-removed"),
                            icon: "check",
                            variant: "success",
                        },
                    });
                });

        const filteredContacts = computed(() => {
            if (!contactsList.value) {
                return [];
            }

            return contactsList.value.contacts.filter((contact) => {
                if (!contact.name.toLowerCase().includes(searchQuery.value.toLowerCase())) {
                    return false;
                }
                // Else if for clarity
                if (filters.value.labels.length && filters.value.filterOption === "or") {
                    return contact.labels?.some((label) => filters.value.labels.includes(label.id!));
                } else if (filters.value.labels.length && filters.value.filterOption === "and") {
                    const contactLabelIds = contact.labels?.map((label) => label.id);
                    return filters.value.labels.every((labelId) => contactLabelIds.includes(labelId));
                }

                return true;
            });
        });

        const selectedFilteredContacts = computed(() =>
            filteredContacts.value.filter((contact) => currentSelected.value.includes(contact.id))
        );

        const indeterminate = computed(
            () =>
                !!selectedFilteredContacts.value.length &&
                selectedFilteredContacts.value.length !== filteredContacts.value.length
        );

        watch(
            [selectedFilteredContacts, indeterminate],
            () => {
                if (indeterminate.value) {
                    return (selectedAll.value = false);
                }

                if (!selectedFilteredContacts.value.length) {
                    return (selectedAll.value = false);
                }

                if (selectedFilteredContacts.value.length === filteredContacts.value.length) {
                    return (selectedAll.value = true);
                }
            },
            { deep: true }
        );

        watch(
            [searchQuery, filters],
            () => {
                nextTick(() => refreshSelection());
            },
            { deep: true }
        );

        const toggleSelectAll = (selectAll: boolean) => {
            if (selectAll) {
                filteredContacts.value.forEach((contact) => {
                    if (currentSelected.value.includes(contact.id)) {
                        return;
                    }

                    currentSelected.value.push(contact.id);
                });
            } else {
                currentSelected.value = currentSelected.value.filter(
                    (selectedContactId) => !filteredContacts.value.find((contact) => contact.id === selectedContactId)
                );
            }

            nextTick(() => refreshSelection());
        };

        return {
            // Filters
            searchQuery,
            filteredContacts,
            filters,

            // Select all
            indeterminate,
            selectedAll,
            toggleSelectAll,

            // Bootstrap table
            contactsTable,
            columns,
            clickedRow,
            styleByColor,
            refreshSelection,

            // Contact management
            currentSelected,
            contactsList,
            updateSelection,
            updateContacts,
            removeContacts,
        };
    },
});
